import { Inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { degrees, PDFDocument, PDFFont, rgb, RGB } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { saveAs } from 'file-saver';
import { ASSETS_PATH } from '../../injection-tokens';
import { WatermarkConfig, Font, FileConfig, Color } from '../../models/pdf.model';
import { FileDownloadingHelperService } from '../file-downloading-helper/file-downloading-helper.service';

const DEFAULT_WATERMARK_CONFIG: WatermarkConfig = {
  font: Font.MulishBold,
  fontColor: { red: 0, green: 0, blue: 0 },
  fontHeight: 27,
  fontOpacity: 0.1,
  rotationAngle: 45,
  textSeparator: ' | ',
  lineGap: 10,
};

const PAGE_INDENT_X_AXIS = 10;

@Injectable()
export class PdfService {
  private fonts: Record<Font, PDFFont>;

  private readonly http: HttpClient;

  constructor(
    httpBackend: HttpBackend,
    @Inject(ASSETS_PATH) private readonly assetsPath: string,
    private readonly fileDownloadingHelper: FileDownloadingHelperService,
  ) {
    this.http = new HttpClient(httpBackend);
  }

  downloadPdfWithWatermark(file: File, watermark: string[], watermarkConfig?: WatermarkConfig, fileConfig?: FileConfig): void {
    if (!this.isPdf(file)) {
      throw new Error('The file is not a PDF.');
    }

    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(file);

    fileReader.onload = async () => {
      const pdfBuffer = new Uint8Array(fileReader.result as ArrayBuffer);
      const isEncrypted = await this.isPdfEncrypted(pdfBuffer);

      if (isEncrypted) {
        this.fileDownloadingHelper.downloadFile(file, fileConfig.customFileName);
        return;
      }

      const pdfDoc = await this.initializePdf(pdfBuffer);
      this.addWatermark(pdfDoc, watermark, watermarkConfig);
      await this.savePdf(pdfDoc, file, fileConfig);
    };
  }

  private isPdf(file: File): boolean {
    const fileType = file.name?.split('.').reverse()[0];

    return fileType?.toLowerCase() === 'pdf';
  }

  private async initializePdf(pdf: Uint8Array): Promise<PDFDocument> {
    const pdfDoc = await PDFDocument.load(pdf);

    pdfDoc.registerFontkit(fontkit);
    await this.initializeFonts(pdfDoc);

    return pdfDoc;
  }

  private addWatermark(pdfDoc: PDFDocument, watermark: string[], watermarkConfig: WatermarkConfig): void {
    const textConfig: WatermarkConfig = {
      font: watermarkConfig?.font || DEFAULT_WATERMARK_CONFIG.font,
      fontColor: watermarkConfig?.fontColor || DEFAULT_WATERMARK_CONFIG.fontColor,
      fontSize: watermarkConfig?.fontSize,
      fontHeight: watermarkConfig?.fontSize ? undefined : (watermarkConfig?.fontHeight || DEFAULT_WATERMARK_CONFIG.fontHeight),
      fontOpacity: watermarkConfig?.fontOpacity || DEFAULT_WATERMARK_CONFIG.fontOpacity,
      rotationAngle: watermarkConfig?.rotationAngle || DEFAULT_WATERMARK_CONFIG.rotationAngle,
      textSeparator: watermarkConfig?.textSeparator || DEFAULT_WATERMARK_CONFIG.textSeparator,
      lineGap: watermarkConfig?.lineGap || DEFAULT_WATERMARK_CONFIG.lineGap,
    };

    const font = this.fonts[textConfig.font];
    const fontSize = textConfig.fontSize || font.sizeAtHeight(textConfig.fontHeight);
    const fontColor = this.getColor(textConfig.fontColor);
    const fontOpacity = textConfig.fontOpacity;

    const pages = pdfDoc.getPages();
    const { width: pageWidth, height: pageHeight } = pages[0].getSize();
    const availablePageWidth = pageWidth - PAGE_INDENT_X_AXIS * 2;

    const pageCenterX = pageWidth / 2;
    const pageCenterY = pageHeight / 2;

    const watermarkLines = this.splitTextIntoLines(
      watermark,
      availablePageWidth,
      font,
      fontSize,
      textConfig.textSeparator,
      textConfig.rotationAngle,
    );
    const watermarkHeight = font.heightAtSize(fontSize);

    const centeringLinesHeight = (watermarkLines.length * watermarkHeight) / 2;

    for (const page of pages) {
      for (let i = 0; i < watermarkLines.length; i++) {
        const line = watermarkLines[i];
        const lineWidth = this.getTextWidth(line, font, fontSize, textConfig.rotationAngle);
        const lineHeight = this.getTextHeight(line, font, fontSize, textConfig.rotationAngle);
        const lineGap = i > 0 ? textConfig.lineGap : 0;
        const lineShift = watermarkHeight * i;

        const watermarkX = pageCenterX - (lineWidth / 2);
        const watermarkY = pageCenterY - (lineHeight / 2) + centeringLinesHeight - lineShift - lineGap;

        page.drawText(line, {
          x: watermarkX,
          y: watermarkY,
          font,
          size: fontSize,
          color: fontColor,
          opacity: fontOpacity,
          rotate: degrees(textConfig.rotationAngle),
        });
      }
    }
  }

  private async savePdf(pdfDoc: PDFDocument, file: File, fileConfig: FileConfig): Promise<void> {
    const fileName = fileConfig?.customFileName ? `${fileConfig.customFileName}.pdf` : file.name;
    const pdfBytes = await pdfDoc.save();
    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

    saveAs(pdfBlob, fileName);
  }

  private async initializeFonts(pdfDoc: PDFDocument): Promise<void> {
    const mulishRegular = await this.loadFont(pdfDoc, Font.MulishRegular);
    const mulishBold = await this.loadFont(pdfDoc, Font.MulishBold);

    this.fonts = {
      [Font.MulishRegular]: mulishRegular,
      [Font.MulishBold]: mulishBold,
    };
  }

  private async loadFont(pdfDoc: PDFDocument, fontName: string): Promise<PDFFont> {
    const fontBuffer = await firstValueFrom(
      this.http.get(`${this.assetsPath}/fonts/mulish/${fontName}.ttf`, { responseType: 'arraybuffer' })
    );
    const pdfFont = await pdfDoc.embedFont(fontBuffer);

    return pdfFont;
  }

  private getColor({ red, green, blue }: Color): RGB {
    return rgb(red / 255, green / 255, blue / 255);
  }

  private splitTextIntoLines(
    text: string[],
    maxWidth: number,
    font: any,
    fontSize: number,
    textSeparator: string,
    rotationAngle: number,
  ): string[] {
    if (!text.length) {
      return text;
    }

    const lines = [];
    let currentLine = text[0];

    if (text.length === 1) {
      return [currentLine];
    }

    const canBePlacedInline = this.getTextWidth(text.join(textSeparator), font, fontSize, rotationAngle) < maxWidth;

    if (canBePlacedInline) {
      return [text.join(textSeparator)];
    }

    for (let i = 1; i < text.length; i++) {
      const width = this.getTextWidth(`${currentLine}${textSeparator}${text[i]}`, font, fontSize, rotationAngle);

      if (width < maxWidth) {
        currentLine += `${textSeparator}${text[i]}`;
      } else {
        lines.push(currentLine);
        currentLine = `${textSeparator}${text[i]}`;
      }
    }

    lines.push(currentLine);

    return lines;
  }

  private getTextWidth(text: string, font: PDFFont, fontSize: number, rotationAngle?: number): number {
    const fontWidth = font.widthOfTextAtSize(text, fontSize);

    if (!rotationAngle) {
      return fontWidth;
    }

    const { angle } = degrees(rotationAngle);
    const cosAngle = Math.cos(angle * Math.PI / 180);

    return fontWidth * cosAngle;
  }

  private getTextHeight(text: string, font: PDFFont, fontSize: number, rotationAngle?: number): number {
    const { angle } = degrees(rotationAngle);
    const sinAngle = Math.sin(angle * Math.PI / 180);
    const cosAngle = Math.cos(angle * Math.PI / 180);
    const textWidth = font.widthOfTextAtSize(text, fontSize);
    const textHeight = font.heightAtSize(fontSize);

    return textWidth * sinAngle + textHeight * cosAngle;
  }

  private async isPdfEncrypted(pdf: Uint8Array): Promise<boolean> {
    try {
      await PDFDocument.load(pdf);
      return false;
    } catch {
      return true;
    }
  }
}
